/* Company role definitions — single source of truth for the
 * "What can I do?" help dialog and any future role-based UX.
 *
 * Mirrors `specs/company_role_functions.md`. When that doc changes,
 * update this file in lockstep.
 *
 * All visible strings are i18n keys (translated via svelte-i18n →
 * django-rosetta). The keys in this module are deliberately structural
 * rather than literal text — translators see the key plus an English
 * reference comment in rosetta.
 */

import type { AuthState } from "./auth";

export type CompanyType = "manufacturer" | "wholesaler" | "shop";

/** A company that makes what it sells and buys nothing in to resell — a
 *  manufacturer and no wholesaler or shop besides. Its «Цены» sheet starts
 *  from the production cost («Себестоимость»), not a purchase price, and the
 *  landed-cost build-up (transport %, customs %) is not its question
 *  (owner, 2026-09-22). */
export function makesItsGoods(state: AuthState, slug: string): boolean {
  const t = state.memberships.find((m) => m.tenant.slug === slug)?.tenant;
  const types = t?.company_types?.length ? t.company_types : [t?.company_type ?? ""];
  return types.includes("manufacturer") && !types.some((x) => x === "wholesaler" || x === "shop");
}

/** Who each company type is publicly recruiting, and which market sets the
 *  Search Requests screen should therefore offer.
 *
 *  Goods flow manufacturer → wholesaler → shop, so "who am I looking for"
 *  differs per role and a manufacturer must never be shown a "looking for a
 *  manufacturer" tab (or a shop a "looking for shops" one). Both the public
 *  page's CTA section and the Search Requests tab strip read from here so the
 *  two can't drift apart.
 *
 *  `marketTabs` names the tenant market fields, in display order:
 *    represented    → represented_markets
 *    target         → target_markets                 (distributors / wholesalers)
 *    manufacturing  → target_manufacturing_markets
 *    retail         → target_retail_markets
 *
 *  `labelKeys` overrides the default tab label per role — a shop's
 *  `target` set reads "looking for a wholesaler", not "for a distributor",
 *  and its `represented` set is where its own stores are.
 */
export type MarketTab = "represented" | "target" | "manufacturing" | "retail";

/** Every market set, in display order. Each company type gets all four.
 *
 *  They used to be filtered per role on the theory that "a manufacturer is not
 *  looking for a manufacturer". That is wrong often enough to be a bug:
 *  contract production, a producer that also wholesales, a shop opening its own
 *  line. Withholding the tab didn't stop those companies existing, it just left
 *  them with nowhere to say what they were looking for. */
const ALL_MARKET_TABS: MarketTab[] = ["represented", "target", "manufacturing", "retail"];

export const ROLE_MARKETS: Record<
  CompanyType,
  { tabs: MarketTab[]; labelKeys: Partial<Record<MarketTab, string>>; ctaKey: string }
> = {
  manufacturer: {
    tabs: ALL_MARKET_TABS,
    labelKeys: {},
    ctaKey: "manufacturer",
  },
  wholesaler: {
    tabs: ALL_MARKET_TABS,
    labelKeys: {},
    ctaKey: "wholesaler",
  },
  shop: {
    tabs: ALL_MARKET_TABS,
    // A shop's own "represented" set is where its stores are, and the
    // distributors it looks for are wholesalers.
    labelKeys: {
      represented: "market.tab.represented.shop",
      target: "market.tab.target.shop",
    },
    ctaKey: "shop",
  },
};

/** Narrow an arbitrary company_type string to a known role, defaulting to
 *  manufacturer the way the rest of the app does. */
export function asCompanyType(value: string | null | undefined): CompanyType {
  return value === "wholesaler" || value === "shop" ? value : "manufacturer";
}

/** Every type a company holds, primary first, from either shape of tenant
 *  payload. Older sessions (and any caller that only carries `company_type`)
 *  fall back to a one-element list, so consumers never have to branch. */
export function companyTypesOf(
  tenant: { company_type?: string; company_types?: string[] } | null | undefined,
): CompanyType[] {
  const list = (tenant?.company_types?.length
    ? tenant.company_types
    : [tenant?.company_type ?? ""]
  ).filter(Boolean);
  const known = list.filter(
    (t): t is CompanyType => t === "manufacturer" || t === "wholesaler" || t === "shop",
  );
  return known.length ? [...new Set(known)] : ["manufacturer"];
}

/** Each role's short headline + the navigation it owns (subset of the
 *  AppShell sidebar). Keys: `roleHelp.role.<id>`, `roleHelp.intro.<id>`,
 *  and entries in `nav` are AppShell item ids. The dialog cross-refs
 *  them against `NAV_LABELS` below to render translated labels. */
export const ROLE_INFO: Record<CompanyType, {
  introKey: string;
  titleKey: string;
  nav: string[];
}> = {
  manufacturer: {
    titleKey: "roleHelp.role.manufacturer",
    introKey: "roleHelp.intro.manufacturer",
    nav: [
      "dashboard",
      "products",
      "product-categories",
      "orders",
      "distributors",
      "documents",
      "company-profile",
      "distribution-points",
      "account",
    ],
  },
  wholesaler: {
    titleKey: "roleHelp.role.wholesaler",
    introKey: "roleHelp.intro.wholesaler",
    nav: [
      "dashboard",
      "products",
      "product-categories",
      "wholesale-catalog",
      "suppliers",
      "orders",
      "cart",
      "distributors",
      "documents",
      "company-profile",
      "distribution-points",
      "account",
    ],
  },
  /* Role adaptation, 2026-08-23 (A300): the shop's sidebar, as it is now
     drawn. The authoritative list is server-side — apps.tenants.surfaces,
     reaching the app through capabilities.surfaces — and this mirrors it so
     the «What can I do?» dialog and the sidebar agree. */
  shop: {
    titleKey: "roleHelp.role.shop",
    introKey: "roleHelp.intro.shop",
    nav: [
      "dashboard",
      "partners",
      "product-categories",
      "products",
      "prices",
      "orders",
      "public-page",
      "account",
    ],
  },
};

/** AppShell nav-item id → i18n key for the visible label. The label
 *  text exists for the authenticated app under `nav.*` (legacy from
 *  the first auth-page i18n pass); we reference it here so the role
 *  dialog stays in sync with the sidebar once the sidebar itself is
 *  internationalized. */
export const NAV_LABEL_KEYS: Record<string, string> = {
  "dashboard":           "nav.dashboard",
  "partners":            "nav.myPartners",
  "prices":              "nav.prices",
  "public-page":         "nav.viewPublicPage",
  "products":            "nav.products",
  "product-categories":  "nav.productCategories",
  "wholesale-catalog":   "nav.wholesaleCatalog",
  "suppliers":           "nav.suppliers",
  "orders":              "nav.orders",
  "cart":                "nav.cart",
  "distributors":        "nav.distributors",
  "documents":           "nav.documents",
  "company-profile":     "nav.companyBranding",
  "distribution-points": "nav.distributionPoints",
  "account":             "nav.account",
};

/** A single row in the function matrix. Each cell's value is itself an
 *  i18n key (one of the `roleHelp.cell.*` set) so translators don't have
 *  to deal with embedded English. */
export type MatrixRow = {
  /** i18n key for the row label (what the feature *is*). */
  labelKey: string;
  manufacturer: string;
  wholesaler: string;
  shop: string;
};

/** Full feature matrix. Mirrors the table in
 *  `specs/company_role_functions.md` row-for-row. */
export const FUNCTION_MATRIX: MatrixRow[] = [
  { labelKey: "roleHelp.row.dashboard",
    manufacturer: "roleHelp.cell.yes",
    wholesaler:   "roleHelp.cell.yes",
    shop:         "roleHelp.cell.yes" },
  { labelKey: "roleHelp.row.companyProfile",
    manufacturer: "roleHelp.cell.yes",
    wholesaler:   "roleHelp.cell.yes",
    shop:         "roleHelp.cell.yes" },
  { labelKey: "roleHelp.row.publicPage",
    manufacturer: "roleHelp.cell.yes",
    wholesaler:   "roleHelp.cell.yes",
    shop:         "roleHelp.cell.optionalLimited" },
  /* A shop keeps the full catalogue tools (2026-08-23): where a supplier is
     not on the platform it enters that supplier's cards itself, tagged with
     the supplier, so its stock is complete. */
  { labelKey: "roleHelp.row.productCatalog",
    manufacturer: "roleHelp.cell.yes",
    wholesaler:   "roleHelp.cell.curatedResale",
    shop:         "roleHelp.cell.yes" },
  { labelKey: "roleHelp.row.productCategories",
    manufacturer: "roleHelp.cell.yes",
    wholesaler:   "roleHelp.cell.yes",
    shop:         "roleHelp.cell.yes" },
  { labelKey: "roleHelp.row.productAssets",
    manufacturer: "roleHelp.cell.yes",
    wholesaler:   "roleHelp.cell.curatedAssets",
    shop:         "roleHelp.cell.yes" },
  { labelKey: "roleHelp.row.warehouses",
    manufacturer: "roleHelp.cell.yes",
    wholesaler:   "roleHelp.cell.yes",
    shop:         "roleHelp.cell.no" },
  { labelKey: "roleHelp.row.suppliers",
    manufacturer: "roleHelp.cell.no",
    wholesaler:   "roleHelp.cell.suppliersManufacturers",
    shop:         "roleHelp.cell.suppliersWholesalers" },
  { labelKey: "roleHelp.row.browseSupplierCatalog",
    manufacturer: "roleHelp.cell.no",
    wholesaler:   "roleHelp.cell.yes",
    shop:         "roleHelp.cell.yes" },
  { labelKey: "roleHelp.row.cart",
    manufacturer: "roleHelp.cell.no",
    wholesaler:   "roleHelp.cell.cartProcurement",
    shop:         "roleHelp.cell.cartSupplier" },
  { labelKey: "roleHelp.row.placeOrders",
    manufacturer: "roleHelp.cell.placeMaybeUpstream",
    wholesaler:   "roleHelp.cell.placeToManufacturers",
    shop:         "roleHelp.cell.placeToWholesalers" },
  { labelKey: "roleHelp.row.receiveOrders",
    manufacturer: "roleHelp.cell.receiveFromBoth",
    wholesaler:   "roleHelp.cell.receiveFromShops",
    shop:         "roleHelp.cell.no" },
  { labelKey: "roleHelp.row.manageOrderStatus",
    manufacturer: "roleHelp.cell.sellerSide",
    wholesaler:   "roleHelp.cell.sellerSide",
    shop:         "roleHelp.cell.buyerTracking" },
  { labelKey: "roleHelp.row.modifyReceivedOrders",
    manufacturer: "roleHelp.cell.sellerSide",
    wholesaler:   "roleHelp.cell.sellerSide",
    shop:         "roleHelp.cell.cancelOnly" },
  { labelKey: "roleHelp.row.distributorsList",
    manufacturer: "roleHelp.cell.yes",
    wholesaler:   "roleHelp.cell.yes",
    shop:         "roleHelp.cell.no" },
  { labelKey: "roleHelp.row.distributionMap",
    manufacturer: "roleHelp.cell.yes",
    wholesaler:   "roleHelp.cell.yes",
    shop:         "roleHelp.cell.no" },
  { labelKey: "roleHelp.row.documents",
    manufacturer: "roleHelp.cell.yes",
    wholesaler:   "roleHelp.cell.yes",
    shop:         "roleHelp.cell.yes" },
  { labelKey: "roleHelp.row.users",
    manufacturer: "roleHelp.cell.ownerManager",
    wholesaler:   "roleHelp.cell.ownerManager",
    shop:         "roleHelp.cell.ownerManager" },
  { labelKey: "roleHelp.row.translations",
    manufacturer: "roleHelp.cell.platformOnly",
    wholesaler:   "roleHelp.cell.no",
    shop:         "roleHelp.cell.no" },
];
